import { IReportMeasurement, IReportMeasurementGroup } from 'Data';
import { Parameter, UnitPresentation, GetParameter } from 'MeasurementsCalculations';

export class ParameterHelper {
  static getParameterValue(
    measurement: IReportMeasurement,
    propertyName: string,
    unitPresentation: UnitPresentation
  ) {
    const propertyNameCamelCase = this.firstLetterToLowerCase(propertyName);
    if (
      measurement[propertyNameCamelCase] !== null &&
      measurement[propertyNameCamelCase] !== undefined
    ) {
      return unitPresentation.toString(
        GetParameter(propertyName),
        measurement[propertyNameCamelCase]
      );
    } else {
      return '-';
    }
  }

  static getParameterValueFromGroup(
    group: IReportMeasurementGroup,
    propertyName: string,
    unitPresentation: UnitPresentation
  ) {
    const propertyNameCamelCase = this.firstLetterToLowerCase(propertyName);

    if (group[propertyNameCamelCase] !== null && group[propertyNameCamelCase] !== undefined) {
      const value = +group[propertyNameCamelCase];
      return unitPresentation.toString(GetParameter(propertyName), value);
    } else {
      return '-';
    }
  }

  static getParameterUnit(propertyName: string, unitPresentation: UnitPresentation) {
    return unitPresentation.displayUnit(GetParameter(propertyName));
  }

  static firstLetterToLowerCase = (parameterText: string) => {
    return parameterText.substr(0, 1).toLowerCase() + parameterText.substr(1);
  };

  static firstLetterToUpperCase = (parameterText: string) => {
    return parameterText.substr(0, 1).toUpperCase() + parameterText.substr(1);
  };
  static getComputedParameterAverage(
    strokes: Array<IReportMeasurement>,
    parameter: string,
    unitPresentation: UnitPresentation,
    showAverageInTopRow?: boolean
  ): string | number {
    const propertyNameCamelCase = ParameterHelper.firstLetterToLowerCase(parameter);

    let sum = 0;
    let numberOfElems = 0;
    for (let i = 0; i < strokes.length; i++) {
      if (
        strokes[i] &&
        strokes[i][propertyNameCamelCase] !== null &&
        strokes[i][propertyNameCamelCase] !== undefined
      ) {
        if (strokes[i].isVisible) {
          numberOfElems++;
          sum += strokes[i][propertyNameCamelCase];
        }
      }
    }

    if (showAverageInTopRow) {
      if (numberOfElems !== 0) {
        return sum / numberOfElems;
      } else {
        return null;
      }
    } else {
      if (numberOfElems !== 0) {
        return unitPresentation.toString(Parameter[parameter], sum / numberOfElems);
      } else {
        return '-';
      }
    }
  }

  static calculateAverageOnSetOfData(setOfNumbers: Array<number>): number {
    let sum = 0;
    for (let i = 0; i < setOfNumbers.length; i++) {
      sum += setOfNumbers[i];
    }
    return sum / setOfNumbers.length;
  }
  static calculateDifferencesFromAverageOnSetOfData(
    setOfNumbers: Array<number>,
    average: number
  ): Array<number> {
    const arrayWithDifferencesFromAverage: Array<number> = [];
    for (let i = 0; i < setOfNumbers.length; i++) {
      const diffferenceBetweenValuesAndAverage = setOfNumbers[i] - average;
      arrayWithDifferencesFromAverage.push(diffferenceBetweenValuesAndAverage);
    }
    return arrayWithDifferencesFromAverage;
  }
  static calculateSquaredValuesOnSetOfData(setOfNumbers: Array<number>) {
    const arrayWithSquaredValues: Array<number> = [];
    for (let i = 0; i < setOfNumbers.length; i++) {
      arrayWithSquaredValues.push(Math.pow(setOfNumbers[i], 2));
    }
    return arrayWithSquaredValues;
  }

  //Consistency
  static calculateStandardDeviation(setOfNumbers: Array<number>, average: number) {
    //Variance is the average of the squared differences from the Mean
    //Standard deviation is square root of variance
    const calculateDifferencesFromAverageOnSetOfData = ParameterHelper.calculateDifferencesFromAverageOnSetOfData(
      setOfNumbers,
      average
    );
    const calculateSquaredValuesOnSetOfData = ParameterHelper.calculateSquaredValuesOnSetOfData(
      calculateDifferencesFromAverageOnSetOfData
    );
    const calculateVarianceOnSetOfData = ParameterHelper.calculateAverageOnSetOfData(
      calculateSquaredValuesOnSetOfData
    );
    return Math.sqrt(calculateVarianceOnSetOfData);
  }

  static getComputedParameterStandardDeviation(
    strokes: Array<IReportMeasurement>,
    parameter: string,
    unitPresentation: UnitPresentation
  ): string {
    const propertyNameCamelCase = ParameterHelper.firstLetterToLowerCase(parameter);

    const arrayWithSpecificParameterData: Array<number> = [];
    for (let i = 0; i < strokes.length; i++) {
      if (
        strokes[i][propertyNameCamelCase] !== null &&
        strokes[i][propertyNameCamelCase] !== undefined
      ) {
        if (strokes[i].isVisible) {
          arrayWithSpecificParameterData.push(strokes[i][propertyNameCamelCase]);
        }
      }
    }
    if (arrayWithSpecificParameterData.length > 1) {
      const standardDeviation = ParameterHelper.calculateStandardDeviation(
        arrayWithSpecificParameterData,
        ParameterHelper.calculateAverageOnSetOfData(arrayWithSpecificParameterData)
      );
      if (
        GetParameter(parameter) === Parameter.Side ||
        GetParameter(parameter) === Parameter.SideTotal
      )
        return unitPresentation.formatValue(GetParameter(parameter), standardDeviation) ?? '-';
      else return unitPresentation.toString(GetParameter(parameter), standardDeviation);
    } else {
      return '-';
    }
  }
  static getParameterLink(parameter: string) {
    if (
      parameter === 'ClubSpeed' ||
      parameter === 'SmashFactor' ||
      parameter === 'BallSpeed' ||
      parameter === 'LaunchAngle' ||
      parameter === 'SpinRate' ||
      parameter === 'Height' ||
      parameter === 'LandingAngle' ||
      parameter === 'Carry' ||
      parameter === 'Total'
    ) {
      if (parameter === 'Total') {
        return `${process.env.REACT_APP_GOLF_PORTAL_URL}/parameters/estimatedtotal`;
      } else {
        return (
          `${process.env.REACT_APP_GOLF_PORTAL_URL}/parameters/` +
          parameter
            .replace(/(?:^|\.?)([A-Z])/g, function(x, y) {
              return y.toLowerCase();
            })
            .replace(/^-/, '')
        );
      }
    } else {
      return '';
    }
  }
}
